/**
 * Tour Steps Configuration for Dashboard TourGuide
 * This file defines all the tour steps for the application
 * Static values are used here - can be replaced with API calls later
 */
import React from 'react';
import { useTour } from '@reactour/tour';
import { markTourAsCompleted } from './helpers/tourGuideHelper';

const StepCounter = ({ current }) => {
	const { steps: activeSteps } = useTour();
	return <>{current} of {activeSteps ? activeSteps.length : ''}</>;
};

const steps = [
	{
		selector: '[data-tour="dashboard-welcome"]',
		content: ({ setIsOpen, currentStep, steps, setCurrentStep }) => {
			const isDarkTheme = document.documentElement.getAttribute('theme') === 'dark';
			return (
				<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
					<div style={{ marginBottom: '20px' }}>
						<h2 style={{
							margin: '0 0 10px 0',
							fontSize: '26px',
							fontWeight: '700',
							color: '#1a1a1a',
							background: 'linear-gradient(135deg, #3aba6f 0%, #2d8f52 100%)',
							WebkitBackgroundClip: 'text',
							WebkitTextFillColor: 'transparent',
							backgroundClip: 'text',
						}}>
							👋 Welcome!
						</h2>
						<p style={{
							margin: '0',
							fontSize: '13px',
							color: '#999999',
							fontWeight: '500',
							textTransform: 'uppercase',
							letterSpacing: '0.5px',
						}}>
							Step <StepCounter current={currentStep + 1} />
						</p>
					</div>
					<p style={{
						margin: '0 0 24px 0',
						fontSize: '15px',
						lineHeight: '1.8',
						color: '#4b5563',
						fontWeight: '500',
					}}>
						I'm your personal assistant. Let me give you a quick tour of all the features available on your dashboard.
					</p>
					<div style={{ 
						marginTop: '24px', 
						display: 'flex', 
						gap: '12px',
						justifyContent: 'space-between',
					}}>
						<button
							onClick={() => setIsOpen(false)}
							style={{
								padding: '10px 20px',
								backgroundColor: '#f0f0f0',
								color: '#1a1a1a',
								border: 'none',
								borderRadius: '8px',
								cursor: 'pointer',
								fontSize: '14px',
								fontWeight: '600',
								transition: 'all 0.3s ease',
								flex: 1,
								textAlign: 'center',
							}}
							onMouseEnter={(e) => {
								e.target.style.backgroundColor = '#e0e0e0';
								e.target.style.transform = 'translateY(-1px)';
							}}
							onMouseLeave={(e) => {
								e.target.style.backgroundColor = '#f0f0f0';
								e.target.style.transform = 'translateY(0)';
							}}
						>
							Skip Tour
						</button>
						<button
							onClick={() => setCurrentStep(currentStep + 1)}
							style={{
								padding: '10px 20px',
								backgroundColor: '#3aba6f',
								color: 'white',
								border: 'none',
								borderRadius: '8px',
								cursor: 'pointer',
								fontSize: '14px',
								fontWeight: '600',
								transition: 'all 0.3s ease',
								flex: 1,
								textAlign: 'center',
								boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
							}}
							onMouseEnter={(e) => {
								e.target.style.transform = 'translateY(-2px)';
								e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
							}}
							onMouseLeave={(e) => {
								e.target.style.transform = 'translateY(0)';
								e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
							}}
						>
							Next →
						</button>
					</div>
				</div>
			);
		},
	},
	{
		selector: '[data-tour="pending-appointments"]',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Pending Appointments</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This card shows all your upcoming appointments that are pending. Click on it to view the full list and manage your schedule.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '[data-tour="completed-appointments"]',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>✅ Completed Appointments</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					View your completed appointments here. This helps you track your business performance and customer satisfaction.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '[data-tour="cancelled-appointments"]',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>❌ Cancelled Appointments</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This section shows all your cancelled appointments. You can review and analyze cancellation patterns to improve your business.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '[data-tour="expired-appointments"]',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>⏰ Expired Appointments</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					View appointments that have expired. This helps you understand appointment fulfillment and identify patterns.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '[data-tour="add-order-button"]',
		content: ({ setCurrentStep, currentStep, setIsOpen, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>➕ Add Order</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /> - Final</p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use this button to quickly add new orders to your system. You can create new appointments or products from here.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => { markTourAsCompleted(); setIsOpen(false); }}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-filter-section',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use filters to narrow pending appointments by employee, branch, service, or date range.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-pending-table',
		content: ({ setCurrentStep, currentStep, steps, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Pending Appointments Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all pending appointments with key details and quick actions to manage them.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-completed-filter-section',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use filters to narrow completed appointments by employee, branch, service, or date range.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-completed-table',
		content: ({ setCurrentStep, currentStep, steps, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>✅ Completed Appointments Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all completed appointments with key details and quick actions to manage them.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-cancelled-filter-section',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use filters to narrow cancelled appointments by employee, branch, service, or date range.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-cancelled-table',
		content: ({ setCurrentStep, currentStep, steps, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>❌ Cancelled Appointments Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all cancelled appointments with key details. You can review cancellation reasons and analyze patterns to improve your business.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-expired-filter-section',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use filters to narrow expired appointments by employee, branch, service, or date range.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-expired-table',
		content: ({ setCurrentStep, currentStep, steps, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>⏰ Expired Appointments Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all expired appointments. Review them to understand appointment fulfillment and identify patterns.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-order-new-order-btn',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>➕ New Order</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click this button to create a new order. You can add appointments, products, apply promo codes, and manage customer details all in one place.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-order-filter-section',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use filters to search and narrow orders by customer, branch, employee, date range, or order status.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-order-table',
		content: ({ setCurrentStep, currentStep, steps, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🛒 Orders Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all your orders with key details. Click on any order to view full details, manage payments, or take actions.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-service-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>➕ Add Service</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click this button to add a new service. You can set the name, price, duration, category, and more.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-service-search',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Search Services</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use the search box to quickly find services by name or category.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-service-grid',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🗂️ Services Grid</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					All your services are displayed here. Click on a service card to edit, change its status, or manage its details.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-category-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>➕ Add Category</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click this button to add a new service category. Categories help you organise your services.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-category-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Search & Filter</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use the search bar to quickly find categories by name, or adjust the number of items displayed per page.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-category-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🗂️ Categories Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all your service categories. Click the edit icon on any row to update the category name or status.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-product-upload-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📤 Upload Product List</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use this button to bulk upload products via a CSV or ZIP file. This makes it easy to add many products at once.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-product-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📦 New Product</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click here to add a new product. Set the name, price, branch, images, and more.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-product-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Search & Filter</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Filter products by branch or search by name to quickly find what you need.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-product-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🛍️ Products Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					All your products are listed here. You can edit, activate, deactivate, or delete products, and select multiple items for bulk actions.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-promotion-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🎁 New Promotion</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click here to create a new promotion. Set a promo code, discount, applicable services or products, and validity dates.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-promotion-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Search & Filter</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Search for promotions by name or adjust how many items are shown per page.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-promotion-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Promotions Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					All your promotions are listed here. You can view promo codes, date ranges, discounts, and edit or delete any promotion.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-customer-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Search Customers</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use the search bar to find customers by name, email, or other details. Adjust the items per page to control how many are shown at once.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-customer-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>👥 Customers Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					All registered customers are listed here. You can view their details, contact info, and status, or click to edit their profile.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-employee-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>➕ Add New Employee</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click this button to add a new employee. Fill in their name, email, role, branch, and other required details.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-employee-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Search Employees</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Filter employees by branch or use the search bar to find someone by name, email, or other details.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-employee-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>👥 Employees Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					All your employees are listed here with their ID, name, role, email, and status. Click the action icons to edit details, change passwords, or manage their schedule.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-admin-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>➕ Add New Admin</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click this button to add a new administrator. Fill in their name, email, password, role, and other required details.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-admin-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Search Admins</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use the search bar to find administrators by name, email, or other details. You can also adjust how many entries are shown per page.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-admin-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🛡️ Administrators Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					All system administrators are listed here with their name, email, status, and other details. Use the action icons to edit or manage each administrator.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-shift-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>➕ Add New Shift</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click this button to create a new shift. Set the shift name, start time, and end time for your employees.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-shift-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Search Shifts</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Search for shifts by name or adjust how many entries are shown per page.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-shift-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🕐 Shifts Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					All shifts are listed here with their name, start time, end time, and status. Use the action icons to edit or delete a shift.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-worksched-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📅 Schedule Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Select a branch, set the start and end dates, then click <strong>Apply Changes</strong> to load and save the worker schedule for that date range.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-worksched-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🗓️ Worker Schedule</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					The schedule grid shows each employee’s assigned shifts across the selected date range. Click on a date cell to assign or change a shift for that employee.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-leavereq-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔍 Search & Filter</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use the search box to find specific leave requests by employee name or ID. You can also adjust how many records are shown per page.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-leavereq-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Leave Requests List</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all employee leave requests along with the leave type, duration, date, and current status. Click the <strong>Edit</strong> icon on any row to view details and approve or reject the request.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-payroll-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔍 Payroll Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Select a branch and employee, then set the start and end dates for the payroll period. Click <strong>Search</strong> to load the payroll data for that employee.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-payroll-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>💰 Payroll Details</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This section shows the employee's payroll breakdown including base salary, attendance allowance, commissions, and the total payable amount. You can also view payroll history here.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-income-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔍 Income Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Select the date range and other filters to narrow down income records. Click <strong>Search</strong> to load the matching transactions.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-income-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>💵 Income Records</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all income transactions including the transaction ID, order ID or payee, payment date, and amount. Use the pagination controls to browse through records.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
];

export const styles = {
	popover: (base) => ({
		...base,
		backgroundColor: '#ffffff',
		color: '#1a1a1a',
		borderRadius: '12px',
		padding: '24px',
		boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
		maxWidth: '420px',
		fontFamily: 'system-ui, -apple-system, sans-serif',
		transform: 'translateY(0)',
		transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
	}),
	badge: (base) => ({
		...base,
		backgroundColor: '#3aba6f',
		color: '#ffffff',
		fontSize: '12px',
		borderRadius: '6px',
		padding: '6px 10px',
		fontWeight: '600',
	}),
	maskArea: (base) => ({
		...base,
		rx: 12,
		fill: 'rgba(0, 0, 0, 0.15)',
	}),
	maskRect: (base) => ({
		...base,
		fill: 'rgba(0, 0, 0, 0.15)',
	}),
	highlightedArea: (base) => ({
		...base,
		boxShadow: '0 0 0 4px rgba(58, 186, 111, 0.2)',
		borderRadius: '12px',
		transition: 'all 0.3s ease',
	}),
};

export default steps;
